import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Circle, Square } from 'lucide-react';
import clsx from 'clsx';
import type { TeamLiveStreamEntry } from '@/types';

export interface TeamLiveDirectorActionsProps {
  /** Streams that may start recording right now. */
  recordables: TeamLiveStreamEntry[];
  /** Streams that may stop recording right now. */
  stoppables: TeamLiveStreamEntry[];
  /** Device ids that may be switched on right now. */
  camOnDeviceIds: string[];
  /** Device ids that may be switched off right now. */
  camOffDeviceIds: string[];
  onRecordAll: () => void;
  onStopAll: () => void;
  onCamAllOn: () => void;
  onCamAllOff: () => void;
}

/**
 * The director action cluster of the Team Live header (record/stop all,
 * cam all on/off). Extracted from TeamLivePage for the mobile layout work
 * so the page file stays under the 300-line guideline; behavior identical.
 * Bulk cam-off keeps its two-click arm confirm — destructive across cars.
 */
export function TeamLiveDirectorActions({
  recordables,
  stoppables,
  camOnDeviceIds,
  camOffDeviceIds,
  onRecordAll,
  onStopAll,
  onCamAllOn,
  onCamAllOff,
}: TeamLiveDirectorActionsProps) {
  const { t } = useTranslation('pages');
  const [camAllOffArmed, setCamAllOffArmed] = useState(false);
  useEffect(() => {
    if (!camAllOffArmed) return;
    const timer = setTimeout(() => setCamAllOffArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [camAllOffArmed]);

  return (
    <div
      role="group"
      aria-label={t('teamLive.directorActions')}
      className="flex items-center gap-2"
    >
      <button
        type="button"
        disabled={recordables.length === 0}
        onClick={onRecordAll}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
      >
        <Circle className="h-3.5 w-3.5 fill-current" />
        {t('teamLive.recordAll')}
      </button>
      <button
        type="button"
        disabled={stoppables.length === 0}
        onClick={onStopAll}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-white/20 px-4 text-sm font-semibold text-white/80 hover:bg-white/10 disabled:opacity-40"
      >
        <Square className="h-3.5 w-3.5" />
        {t('teamLive.stopAll')}
      </button>
      <button
        type="button"
        disabled={camOnDeviceIds.length === 0}
        onClick={onCamAllOn}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-40"
      >
        <Camera className="h-3.5 w-3.5" />
        {t('teamLive.camAllOn')}
      </button>
      <button
        type="button"
        disabled={camOffDeviceIds.length === 0}
        onClick={() => {
          if (camAllOffArmed) {
            setCamAllOffArmed(false);
            onCamAllOff();
          } else {
            setCamAllOffArmed(true);
          }
        }}
        className={clsx(
          'inline-flex min-h-[44px] items-center gap-2 rounded-lg px-4 text-sm font-semibold disabled:opacity-40',
          camAllOffArmed
            ? 'bg-amber-400 text-black hover:bg-amber-300'
            : 'border border-white/20 text-white/80 hover:bg-white/10',
        )}
      >
        <Camera className="h-3.5 w-3.5" />
        {camAllOffArmed
          ? t('teamLive.camOffConfirm')
          : t('teamLive.camAllOff')}
      </button>
    </div>
  );
}
